import * as React from "react";
import "./contact-modal.css";
import { useContactModal } from "./contact-modal-context";

/* ──────────────────────────────────────────────────────────────────────────────
   ContactModal
   ------------------------------------------------------------------------------
   Behaviour:
   - Modal open/close controlled via ContactModalContext (isOpen / closeContact)
   - Locks body scroll + dims page via body class while open
   - Closes on:
     - Escape
     - Click outside the modal box
   - Submits to Netlify Forms via POST "/" (x-www-form-urlencoded)
   - On submit:
     - Close modal
     - Show toast after modal fade-out completes (prevents “shape jump”)
     - Toast auto-hides after a fixed duration, then clears content post fade-out
   ────────────────────────────────────────────────────────────────────────────── */

/* Timing constants (must match CSS transitions) */
const MODAL_FADE_OUT_MS = 260; // matches .contact-modal opacity transition
const TOAST_FADE_MS = 260; // matches .contact-toast opacity transition
const TOAST_DURATION_MS = 3000;

/* ──────────────────────────────────────────────────────────────
   Utilities
   ────────────────────────────────────────────────────────────── */

/**
 * Convert a plain object to application/x-www-form-urlencoded
 * (Netlify Forms expects this when posting to "/")
 */
function encodeFormBody(data) {
  return Object.keys(data)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
    .join("&");
}

/**
 * Small helper for timeouts with cleanup safety.
 * Returns the timeout id for later clearTimeout.
 */
function wait(ms, cb) {
  return window.setTimeout(cb, ms);
}

export default function ContactModal() {
  const { isOpen, closeContact } = useContactModal();

  /* ──────────────────────────────────────────────────────────────
     Refs
     ────────────────────────────────────────────────────────────── */

  // Used to detect outside clicks (close when clicking outside the box)
  const modalBoxRef = React.useRef(null);

  // Stores the active toast timer so we can cancel/replace it cleanly
  const toastTimerRef = React.useRef(null);

  // Stores “inner” timeouts used by showToast() so we can clear them on unmount
  const toastInnerTimersRef = React.useRef([]);

  /* ──────────────────────────────────────────────────────────────
     State
     ────────────────────────────────────────────────────────────── */

  // idle | sending (kept small for clarity)
  const [status, setStatus] = React.useState("idle");

  // Toast content is separate from visibility so we can:
  // - hide -> swap content -> show (no layout/shape jump)
  const [toastContent, setToastContent] = React.useState(null); // { type: "success"|"error", message: string } | null
  const [toastVisible, setToastVisible] = React.useState(false);

  const isSending = status === "sending";

  /* ──────────────────────────────────────────────────────────────
     Side-effects: body scroll lock + dim state
     ────────────────────────────────────────────────────────────── */
  React.useEffect(() => {
    if (!isOpen) return;

    const body = document.body;
    const prevOverflow = body.style.overflow;

    // Lock scroll + activate your "dim content" styling (via .contact-open)
    body.style.overflow = "hidden";
    body.classList.add("contact-open");

    return () => {
      body.style.overflow = prevOverflow;
      body.classList.remove("contact-open");
    };
  }, [isOpen]);

  /* ──────────────────────────────────────────────────────────────
     Close on Escape
     ────────────────────────────────────────────────────────────── */
  React.useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeContact();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeContact]);

  /* ──────────────────────────────────────────────────────────────
     Click outside to close
     - Uses mousedown so it feels immediate
     ────────────────────────────────────────────────────────────── */
  React.useEffect(() => {
    if (!isOpen) return;

    const onMouseDown = (e) => {
      const box = modalBoxRef.current;
      if (!box) return;

      // If click target isn't inside the box, close
      if (!box.contains(e.target)) closeContact();
    };

    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, closeContact]);

  /* ──────────────────────────────────────────────────────────────
     Cleanup timers on unmount
     ────────────────────────────────────────────────────────────── */
  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);

      // Clear any nested toast timers created inside showToast()
      toastInnerTimersRef.current.forEach((t) => window.clearTimeout(t));
      toastInnerTimersRef.current = [];
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────
     Toast API
     - Hides first, swaps content while hidden, then fades in.
     - After duration: fades out, then clears content AFTER fade completes.
     - delayMs is used to wait for modal fade-out so the UI feels seamless.
     ────────────────────────────────────────────────────────────── */
  const showToast = React.useCallback((type, message, delayMs = 0) => {
    // Cancel any existing display cycle
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastInnerTimersRef.current.forEach((t) => window.clearTimeout(t));
    toastInnerTimersRef.current = [];

    // Hide first to prevent "shape jump" when content changes
    setToastVisible(false);

    // After optional delay (e.g., modal fade-out), set content then reveal
    const t1 = wait(delayMs, () => {
      setToastContent({ type, message });

      // Next frame ensures CSS transition triggers reliably
      requestAnimationFrame(() => setToastVisible(true));

      // After visible duration: fade out
      toastTimerRef.current = wait(TOAST_DURATION_MS, () => {
        setToastVisible(false);

        // Only clear content after fade-out completes
        const t2 = wait(TOAST_FADE_MS, () => setToastContent(null));
        toastInnerTimersRef.current.push(t2);
      });
    });

    toastInnerTimersRef.current.push(t1);
  }, []);

  /* ──────────────────────────────────────────────────────────────
     Submit handler (Netlify Forms)
     - Posts to "/" using x-www-form-urlencoded.
     - Closes modal first, then toast appears after fade-out.
     ────────────────────────────────────────────────────────────── */
  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSending) return;

    setStatus("sending");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      "form-name": form.getAttribute("name") || "contact",
      name: formData.get("name") || "",
      email: formData.get("email") || "",
      message: formData.get("message") || "",
    };

    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodeFormBody(payload),
      });

      if (!res.ok) throw new Error("Request failed");

      // Close modal immediately, then toast after modal finishes fading out
      closeContact();
      showToast("success", "Message sent.", MODAL_FADE_OUT_MS);

      form.reset();
    } catch (err) {
      closeContact();
      showToast("error", "Something went wrong. Try again.", MODAL_FADE_OUT_MS);
    } finally {
      setStatus("idle");
    }
  };

  /* ──────────────────────────────────────────────────────────────
     Render
     ────────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Modal overlay */}
      <div
        className={`contact-modal ${isOpen ? "is-visible" : ""}`}
        aria-hidden={!isOpen}
      >
        {/* Box (clicks inside should NOT close; outside clicks will) */}
        <div className="contact-modal__box" ref={modalBoxRef}>
          <form
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={onSubmit}
            aria-hidden={!isOpen}
          >
            {/* Netlify form name field (must match form name) */}
            <input type="hidden" name="form-name" value="contact" />

            {/* Honeypot field for bots (hidden in CSS) */}
            <p className="contact-modal__hidden">
              <label>
                Don’t fill this out: <input name="bot-field" />
              </label>
            </p>

            <label className="contact-modal__label">
              Name
              <input
                className="contact-modal__input"
                name="name"
                type="text"
                disabled={!isOpen || isSending}
              />
            </label>

            <label className="contact-modal__label">
              Email
              <input
                className="contact-modal__input"
                name="email"
                type="email"
                required
                disabled={!isOpen || isSending}
              />
            </label>

            <label className="contact-modal__label">
              Message
              <textarea
                className="contact-modal__textarea"
                name="message"
                rows="4"
                required
                disabled={!isOpen || isSending}
              />
            </label>

            <button
              className="contact-modal__submit"
              type="submit"
              disabled={!isOpen || isSending}
            >
              {isSending ? "Sending…" : "Submit"}
            </button>
          </form>
        </div>
      </div>

      {/* Toast (success/error) */}
      <div
        className={[
          "contact-toast",
          toastVisible ? "is-visible" : "",
          toastContent?.type ? `is-${toastContent.type}` : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="status"
        aria-live="polite"
      >
        {toastContent?.message || ""}
      </div>
    </>
  );
}
